import { Helmet } from "react-helmet";

export default function PrivacyPolicy() {
  return (
    <div className="flex justify-center w-full">
      <Helmet>
        <title>
          Privacy Policy | Applied BAS
        </title>
        <meta charset="UTF-8" />
        <meta name="description" content="Applied BAS Privacy Policy - Learn how we collect, use, and protect your personal information when you use our website and building automation services. Your privacy is important to us." />
        <meta name="keywords" content="Applied BAS Privacy Policy, Building Automation Privacy, BAS Data Protection, Privacy Policy Dallas, Applied BAS Terms, Data Privacy BAS, Website Privacy Policy" />
        <meta name="author" content="Applied BAS LLC" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.appliedbas.com/privacy-policy" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.appliedbas.com/privacy-policy" />
        <meta property="og:title" content="Privacy Policy | Applied BAS" />
        <meta property="og:description" content="Applied BAS Privacy Policy - Learn how we collect, use, and protect your personal information when you use our website and services." />
        <meta property="og:image" content="https://www.appliedbas.com/ABAS_logo.png" />
        <meta property="og:site_name" content="Applied BAS" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.appliedbas.com/privacy-policy" />
        <meta name="twitter:title" content="Privacy Policy | Applied BAS" />
        <meta name="twitter:description" content="Applied BAS Privacy Policy - Learn how we collect, use, and protect your personal information." />
        <meta name="twitter:image" content="https://www.appliedbas.com/ABAS_logo.png" />
      </Helmet>
      <div className="w-full px-4 lg:px-8 py-4">
        <div className="max-w-[1250px] m-auto mt-32">
          <h2 id="header-1" className="font-semibold text-lg">
            1. Introduction
          </h2>
          <br />
          <p>
            Applied BAS, LLC (&quot;Company&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website www.appliedbas.com (the &quot;Site&quot;) and use our services. We are registered in Texas, United States and have our registered office at 1819 Firman Dr Suite 113, Richardson, TX 75081.
          </p>
          <br />
          <p>
            Please read this Privacy Policy carefully. If you do not agree with the terms of this Privacy Policy, please do not access the Site or use our services.
          </p>
          <br />
          <h2 id="header-2" className="font-semibold text-lg">
            2. Information We Collect
          </h2>
          <br />
          <p>
            We may collect information about you in a variety of ways. The information we may collect on the Site includes:
          </p>
          <br />
          <h3 className="font-semibold">Personal Data</h3>
          <br />
          <p>
            Personally identifiable information, such as your name, email address, phone number, mailing address, and other information that you voluntarily give to us when you:
          </p>
          <ul className="list-disc ml-8 mt-2">
            <li>Register for our services or create an account</li>
            <li>Contact us through our website, email, or phone</li>
            <li>Request information about our services</li>
            <li>Subscribe to our newsletter or marketing communications</li>
            <li>Onboard for our building automation services</li>
          </ul>
          <br />
          <h3 className="font-semibold">Derivative Data</h3>
          <br />
          <p>
            Information our servers automatically collect when you access the Site, such as your IP address, browser type, operating system, access times, and the pages you have viewed directly before and after accessing the Site.
          </p>
          <br />
          <h3 className="font-semibold">Financial Data</h3>
          <br />
          <p>
            Financial information, such as data related to your payment method (e.g., valid credit card number, card brand, expiration date) that we may collect when you purchase, order, return, exchange, or request information about our services from the Site. We store only very limited, if any, financial information that we collect. Otherwise, all financial information is stored by our payment processor, and you are encouraged to review their privacy policy and contact them directly for responses to your questions.
          </p>
          <br />
          <h3 className="font-semibold">Building and System Data</h3>
          <br />
          <p>
            Information related to your building automation systems, including but not limited to system configurations, alarm data, operational data, and performance metrics. This information is collected to provide and improve our services, including alarm notifications and system monitoring.
          </p>
          <br />
          <h2 id="header-3" className="font-semibold text-lg">
            3. How We Use Your Information
          </h2>
          <br />
          <p>
            Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. Specifically, we may use information collected about you via the Site or our services to:
          </p>
          <br />
          <ul className="list-disc ml-8">
            <li>Create and manage your account</li>
            <li>Process your transactions and send you related information</li>
            <li>Email you regarding your account or order</li>
            <li>Fulfill and manage purchases, orders, payments, and other transactions related to the Site</li>
            <li>Deliver targeted advertising, coupons, newsletters, and promotions, and other information regarding our website and mobile application to you</li>
            <li>Enable user-to-user communications</li>
            <li>Fulfill our services, including providing building automation system implementation, support, and monitoring</li>
            <li>Send you email and SMS notifications regarding alarms and critical system alerts after you have onboarded our services</li>
            <li>Respond to your inquiries and provide customer support</li>
            <li>Generate a personal profile about you to make future visits more personalized</li>
            <li>Increase the efficiency and operation of the Site</li>
            <li>Monitor and analyze usage and trends to improve your experience with the Site</li>
            <li>Notify you of updates to the Site</li>
            <li>Perform other business activities as needed</li>
            <li>Prevent fraudulent transactions, monitor against theft, and protect against criminal activity</li>
            <li>Request feedback and contact you about your use of the Site</li>
            <li>Resolve disputes and troubleshoot problems</li>
            <li>Respond to product and customer service requests</li>
            <li>Send you a newsletter</li>
            <li>Solicit support for the Site</li>
          </ul>
          <br />
          <h2 id="header-4" className="font-semibold text-lg">
            4. Disclosure of Your Information
          </h2>
          <br />
          <p>
            We may share information we have collected about you in certain situations. Your information may be disclosed as follows:
          </p>
          <br />
          <h3 className="font-semibold">By Law or to Protect Rights</h3>
          <br />
          <p>
            If we believe the release of information about you is necessary to respond to legal process, to investigate or remedy potential violations of our policies, or to protect the rights, property, and safety of others, we may share your information as permitted or required by any applicable law, rule, or regulation.
          </p>
          <br />
          <h3 className="font-semibold">Third-Party Service Providers</h3>
          <br />
          <p>
            We may share your information with third parties that perform services for us or on our behalf, including payment processing, data analysis, email delivery, hosting services, customer service, and marketing assistance. These third parties are bound by confidentiality agreements and are prohibited from using your personal information for any purpose other than providing services to us.
          </p>
          <br />
          <h3 className="font-semibold">Business Transfers</h3>
          <br />
          <p>
            We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.
          </p>
          <br />
          <h3 className="font-semibold">Affiliates</h3>
          <br />
          <p>
            We may share your information with our affiliates, in which case we will require those affiliates to honor this Privacy Policy. Affiliates include our parent company and any subsidiaries, joint venture partners, or other companies that we control or that are under common control with us.
          </p>
          <br />
          <h3 className="font-semibold">Business Partners</h3>
          <br />
          <p>
            We may share your information with our business partners to offer you certain products, services, or promotions.
          </p>
          <br />
          <h3 className="font-semibold">Other Third Parties</h3>
          <br />
          <p>
            We may share your information with advertisers and investors for the purpose of conducting general business analysis. We may also share your information with such third parties for marketing purposes, as permitted by law.
          </p>
          <br />
          <h2 id="header-5" className="font-semibold text-lg">
            5. Security of Your Information
          </h2>
          <br />
          <p>
            We use administrative, technical, and physical security measures to help protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse. Any information disclosed online is vulnerable to interception and misuse by unauthorized parties. Therefore, we cannot guarantee complete security if you provide personal information.
          </p>
          <br />
          <h2 id="header-6" className="font-semibold text-lg">
            6. Policy for Children
          </h2>
          <br />
          <p>
            We do not knowingly solicit information from or market to children under the age of 18. If we learn that we have collected personal information from a child under age 18 without verification of parental consent, we will delete that information as quickly as possible. If you become aware of any data we have collected from children under age 18, please contact us using the contact information provided below.
          </p>
          <br />
          <h2 id="header-7" className="font-semibold text-lg">
            7. Your Rights
          </h2>
          <br />
          <p>
            Depending on your location, you may have the following rights regarding your personal information:
          </p>
          <br />
          <ul className="list-disc ml-8">
            <li>
              <strong>The right to access</strong> – You have the right to request copies of your personal data.
            </li>
            <li>
              <strong>The right to rectification</strong> – You have the right to request that we correct any information you believe is inaccurate or complete information you believe is incomplete.
            </li>
            <li>
              <strong>The right to erasure</strong> – You have the right to request that we erase your personal data, under certain conditions.
            </li>
            <li>
              <strong>The right to restrict processing</strong> – You have the right to request that we restrict the processing of your personal data, under certain conditions.
            </li>
            <li>
              <strong>The right to object to processing</strong> – You have the right to object to our processing of your personal data, under certain conditions.
            </li>
            <li>
              <strong>The right to data portability</strong> – You have the right to request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions.
            </li>
            <li>
              <strong>Opt-out rights</strong> – You may opt-out of receiving email and SMS notifications by contacting us or following the opt-out instructions in our communications. However, please note that opting out of critical alarm notifications may impact your ability to respond promptly to system issues.
            </li>
          </ul>
          <br />
          <p>
            If you make a request, we have one month to respond to you. If you would like to exercise any of these rights, please contact us using the contact information provided below.
          </p>
          <br />
          <h2 id="header-8" className="font-semibold text-lg">
            8. Email and SMS Communications
          </h2>
          <br />
          <p>
            After you onboard our services, we may send you email and SMS (text message) notifications regarding alarms and critical system alerts related to your building automation systems. By using our services, you consent to receive these communications. Standard message and data rates may apply to SMS notifications.
          </p>
          <br />
          <p>
            You may opt-out of receiving promotional emails from us by following the unsubscribe link in any promotional email we send you or by contacting us directly. You may also opt-out of SMS notifications by replying &quot;STOP&quot; to any SMS message or by contacting us. However, please note that opting out of critical alarm notifications may impact your ability to respond promptly to system issues that require immediate attention.
          </p>
          <br />
          <p>
            Even if you opt-out of receiving promotional communications, we may still send you service-related communications, including but not limited to alarm notifications, account updates, and information about changes to our services or this Privacy Policy.
          </p>
          <br />
          <h2 id="header-9" className="font-semibold text-lg">
            9. Cookies and Tracking Technologies
          </h2>
          <br />
          <p>
            We may use cookies, web beacons, tracking pixels, and other tracking technologies on the Site to help customize the Site and improve your experience. When you access the Site, your personal information is not collected through the use of tracking technology. Most browsers are set to accept cookies by default. You can remove or reject cookies, but be aware that such action could affect the availability and functionality of the Site.
          </p>
          <br />
          <p>
            We may use cookies and other tracking technologies for the following purposes:
          </p>
          <ul className="list-disc ml-8">
            <li>To help us remember and process your preferences</li>
            <li>To understand and save your preferences for future visits</li>
            <li>To compile aggregate data about site traffic and site interactions</li>
            <li>To provide better site experiences and tools in the future</li>
            <li>To track and understand how you use and interact with our Site</li>
          </ul>
          <br />
          <h2 id="header-10" className="font-semibold text-lg">
            10. Third-Party Websites
          </h2>
          <br />
          <p>
            The Site may contain links to third-party websites and applications of interest, including advertisements and external services, that are not affiliated with us. Once you have used these links to leave the Site, any information you provide to these third parties is not covered by this Privacy Policy, and we cannot guarantee the safety and privacy of your information. Before visiting and providing any information to any third-party websites, you should inform yourself of the privacy policies and practices (if any) of the third party responsible for that website, and should take those steps necessary to, in your discretion, protect the privacy of your information. We are not responsible for the content or privacy and security practices and policies of any third parties, including other sites, services, or applications that may be linked to or from the Site.
          </p>
          <br />
          <h2 id="header-11" className="font-semibold text-lg">
            11. California Privacy Rights
          </h2>
          <br />
          <p>
            California Civil Code Section 1798.83, also known as the &quot;Shine The Light&quot; law, permits our users who are California residents to request and obtain from us, once a year and free of charge, information about categories of personal information (if any) we disclosed to third parties for direct marketing purposes and the names and addresses of all third parties with which we shared personal information in the immediately preceding calendar year. If you are a California resident and would like to make such a request, please submit your request in writing to us using the contact information provided below.
          </p>
          <br />
          <p>
            If you are under 18 years of age, reside in California, and have a registered account with the Site, you have the right to request removal of unwanted data that you publicly post on the Site. To request removal of such data, please contact us using the contact information provided below, and include the email address associated with your account and a statement that you reside in California. We will make sure the data is not publicly displayed on the Site, but please be aware that the data may not be completely or comprehensively removed from our systems.
          </p>
          <br />
          <h2 id="header-12" className="font-semibold text-lg">
            12. Data Retention
          </h2>
          <br />
          <p>
            We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Policy, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements). When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize it, or, if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible.
          </p>
          <br />
          <h2 id="header-13" className="font-semibold text-lg">
            13. Changes to This Privacy Policy
          </h2>
          <br />
          <p>
            We may update this Privacy Policy from time to time in order to reflect, for example, changes to our practices or for other operational, legal, or regulatory reasons. We will notify you of any changes by posting the new Privacy Policy on this page and updating the &quot;Last Updated&quot; date. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
          </p>
          <br />
          <h2 id="header-14" className="font-semibold text-lg">
            14. Contact Us
          </h2>
          <br />
          <p>
            If you have questions or comments about this Privacy Policy, please contact us at:
          </p>
          <br />
          <div className="font-semibold">
            <p>Applied BAS, LLC</p>
            <p>1819 Firman Dr Suite 113</p>
            <p>Richardson, TX 75081</p>
            <p>+1 (469) 676-8749</p>
            <p>info@appliedbas.com</p>
          </div>
        </div>
      </div>
    </div>
  );
}
